import { useMutation } from '@tanstack/react-query';
import { Col, Form, Row } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { DefaultOptionType } from 'antd/es/select';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

import { ISearchRouteComboBox } from 'apis/GraphQL/Queries/saleLineQuery';
import { SaleLineApi } from 'apis/SaleLineApi';
import { SaleTeamApi } from 'apis/SaleTeamApi';
import { FormFilter, SelectForm } from 'components';
import LazySelectForm, {
  ILazySelectData,
  ILazySelectParams,
  initLazySelectData,
} from 'components/Select/LazySelectForm';
import { useUser } from 'contexts/UserProvider';
import { IFilterTeamSaleCb } from 'interfaces/TeamSale/TeamSale';
import Content from 'layouts/Content';

const spanCol = 8;
interface IProps {
  isOpen: boolean;
  plants: any[];
  saleLines: any[];
  defaultFilter: any;
  onCancel: () => void;
  onFinish: (values: any) => void;
}

const FilterSaleLineTeam = forwardRef(
  ({ isOpen, plants, defaultFilter, onCancel, onFinish }: IProps, ref) => {
    const [form] = useForm();
    const { user } = useUser();

    const [optionPlants, setOptionPlants] = useState<DefaultOptionType[]>([]);

    const [routeData, setRouteData] =
      useState<ILazySelectData>(initLazySelectData);
    const [teamSaleData, setTeamSaleData] =
      useState<ILazySelectData>(initLazySelectData);

    const { isPending: getRouteLoading, mutate: getRouteCb } = useMutation({
        mutationFn: (params: ISearchRouteComboBox) =>
          SaleLineApi.getAllRouteComboBox(params),
        onSuccess: (res) => {
          const items = res.data?.getRouteM.items,
            totalItems = res.data?.getRouteM.pageInfo.totalItems;

          setRouteData({
            options: items.map((item: any) => ({
              label: `${item.id} - ${item.name}`,
              value: item.id,
              key: item?.id,
            })),
            totalItems: totalItems,
          });
        },
        onError: (error) => {
          console.log(error);
        },
      }),
      { isPending: getTeamSaleLoading, mutate: getTeamSales } = useMutation({
        mutationFn: (params: IFilterTeamSaleCb) =>
          SaleTeamApi.getAllTeamSaleCb(params),
        onSuccess: (res) => {
          if (res?.data?.getTeamSales) {
            const items = res.data?.getTeamSales?.items ?? [],
              totalItems = res.data?.getTeamSales?.pageInfo?.totalItems ?? 0;

            setTeamSaleData({
              options: items.map((item: any) => ({
                label: `${item?.subId} - ${item?.name}`,
                value: `${item?.subId}.${item?.plantId}`,
                key: item?.id,
              })),
              totalItems: totalItems,
            });
          }
        },
        onError: (error) => {
          console.log(error);
        },
      });

    useImperativeHandle(ref, () => ({ handleCancelFilter: handleCancel }));

    useEffect(() => {
      form.setFieldsValue(defaultFilter);
    }, [defaultFilter, form]);

    useEffect(() => {
      const options: DefaultOptionType[] = plants.map((plant) => ({
        label: plant.id,
        value: plant.id,
      }));

      setOptionPlants(options);
    }, [plants]);

    const handleCancel = () => {
      form.resetFields();
      form.setFieldsValue(defaultFilter);

      onCancel();
    };

    const handleRouteScrollChange = (params: ILazySelectParams) => {
      const plant = form.getFieldValue('plant');
      const routeParams: ISearchRouteComboBox = {
        ...params,
        plantId: plant || defaultFilter.plant,
      };

      getRouteCb(routeParams);
    };

    const handleTeamSaleScrollChange = (params: ILazySelectParams) => {
      const plant = form.getFieldValue('plant');
      const teamSaleParams: IFilterTeamSaleCb = {
        ...params,
        plantId: plant || defaultFilter.plant,
      };

      getTeamSales(teamSaleParams);
    };

    const handlePlantChange = (value: any) => {
      form.setFieldsValue({ line: undefined, team: undefined });
      const params = {
        pageSize: 10,
        ...(value && { plantId: value }),
      };

      getRouteCb(params);
      getTeamSales(params);
    };

    const handleFinish = (values: any) => {
      const [subId, plantId] = (values.team || '').split('.');

      onFinish({
        ...values,
        team: subId || values.team,
        plant: plantId || values.plant,
      });
    };

    return (
      <Content isOpen={isOpen}>
        <FormFilter onFinish={handleFinish} onCancel={handleCancel} form={form}>
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col span={spanCol} className="h-[80px]">
              <Form.Item label="Chi nhánh" name="plant">
                <SelectForm
                  options={optionPlants}
                  disabled={!(user?.isAdmin || user?.isHO)}
                  placeholder="Chọn chi nhánh"
                  onChange={handlePlantChange}
                />
              </Form.Item>
            </Col>
            <Col span={spanCol} className="h-[80px]">
              <Form.Item label="Tuyến bán hàng" name="line">
                <LazySelectForm
                  data={routeData}
                  loading={getRouteLoading}
                  placeholder="Chọn tuyến"
                  onSelectScrollChange={handleRouteScrollChange}
                />
              </Form.Item>
            </Col>
            <Col span={spanCol} className="h-[80px]">
              <Form.Item label="Đội bán hàng" name="team">
                <LazySelectForm
                  data={teamSaleData}
                  placeholder="Chọn đội"
                  loading={getTeamSaleLoading}
                  onSelectScrollChange={handleTeamSaleScrollChange}
                />
              </Form.Item>
            </Col>
          </Row>
        </FormFilter>
      </Content>
    );
  },
);

export default FilterSaleLineTeam;
